import _ from "lodash";
import { measures } from "../../../services/centralStore";
import { convertToFahrenheit, convertToInches } from "../../../services/convertUnits";
import firstCapitalCase from "../../../services/firstCapitalCase";
import { prepareNumber } from "../../../services/prepareNumber";
import { WeatherData } from "../../../templates/fragments/weather-data";

export const chartWidthBreakpoints = {
    globalWindowBreakpoint: 992, // has to be in line with md breakpoint
    smallerScale: 750,
    chartHeightBreakpoints: [[400, 260], [600, 330], [750, 410], ['default', 520]],
    showMonthAbbrev: 1150,
    oddValues: 450,
    roundedNumbers: 750,
    smallerLabelFonts: 600,
    fullWidthChart: 750
};
const widthBreakpoints = chartWidthBreakpoints;

export const defaultChartLabelStyle: any = { fontFamily: '"dunbar-text-normal", sans-serif' }


export const getFontSize = (chartWidth: number) => {
    return (chartWidth > widthBreakpoints.smallerLabelFonts) ? 17 : 14
}

export const updateMeasuresFromSelector = ({ selectedValue, weatherData }: { selectedValue: string, weatherData: WeatherData }) => {
    if (!selectedValue) return null;
    let newState: { [key: string]: boolean } = {}
    for (let key of measures) {
        newState[key] = false;
    }
    switch (selectedValue) {
        case 'detail': {
            newState['wet'] = true;
            newState['sunhrs'] = !!weatherData.sunhrs[0];
        }
        case 'overview': {
            newState['tmin'] = true;
            newState['tmax'] = true;
            newState['sst'] = !!weatherData.sst[0];
            newState['ppt'] = true;
            break;
        }
        default: {
            newState[selectedValue] = true;
        }
    }
    return newState;
}



export const getDomainMapping = ({ domains, selectedUnit, singleMeasure }: { domains: number[][], selectedUnit: string, singleMeasure: string }): string[][] => {
    let domainMapping: string[][]
    if (singleMeasure) {
        return [[singleMeasure]];
    }
    if (selectedUnit == 'F') {
        domainMapping = [['tmin', 'tmax', 'sst', 'rhm'], ['ws', 'sunhrs', 'wet', 'ppt']] //temperature, precipitation, humidity // wind, sun hours, wet days
    }
    else if (domains.length > 2) {
        domainMapping = [['tmin', 'tmax', 'sst'], ['rhm', 'ppt'], ['ws', 'sunhrs', 'wet']] //temperature // precipitation, humidity // wind, sun hours, wet days
    }
    else {
        domainMapping = [['tmin', 'tmax', 'sst', 'ws', 'sunhrs', 'wet'], ['rhm', 'ppt']] //temperature, wet days, sun hours, wind // precipitation, humidity
    }
    return domainMapping;
}


export const prepareDataForChart = ({ measure, selectedUnit, weatherData, domains, domainMapping }: { measure: string, selectedUnit: string, weatherData: WeatherData, domains: ChartDomains, domainMapping: string[][] }): DataForChart[] => {
    let data: DataForChart[];
    let otherDomain = null;

    if (domainMapping[1] && _.includes(domainMapping[1], measure)) {
        otherDomain = domains[1];
    }
    else if (domainMapping.length > 2 && _.includes(domainMapping[2], measure)) {
        otherDomain = domains[2];
    }
    data = (weatherData as any)[measure].map((r: any, i: number): any => { return { x: i + 1, ...calculateChartValue({ measure, selectedUnit, domains, otherDomain, val: r }) } });

    let janDecAvg = (data[0].y + data[11].y) / 2
    data = [{ x: 0.5, y: janDecAvg, label: '', isArtificial: true }, ...data, { x: 12.5, y: janDecAvg, label: null, isArtificial: true }]

    return data;
}

export const calculateChartValue = ({ measure, selectedUnit, domains, otherDomain, val }: { measure: string, selectedUnit: string, domains: ChartDomains, otherDomain?: [number, number], val: number }) => {

    let precision = 0;
    if (_.includes(['sst', 'tmin', 'tmax'], measure)) {
        val = selectedUnit == 'F' ? convertToFahrenheit(val) : val;
        precision = 1;
    }
    if (_.includes(['ws', 'sunhrs', 'wet'], measure)) {
        precision = 1;
    }
    if (measure == 'ppt') {
        val = selectedUnit == 'F' ? convertToInches(val) : val;
        precision = selectedUnit == 'F' ? 2 : 0;
    }
    let originalVal = val;
    val = otherDomain ? convertToDomain({ val, domain: otherDomain, domains }) : val;
    let valFixed = originalVal == null ? null : prepareNumber(originalVal, precision);
    let ret: { y: number, label: string, forTooltip: string, y0?: number } = { y: val, label: valFixed, forTooltip: valFixed }
    if (measure == 'ppt') {
        ret.y0 = domains[0][0] + 0.1;
    }
    return ret;
}

export const showJustOneMeasure = ({ measure, measuresShown, chartData }: { measure: string, measuresShown: string[], chartData: { [key: string]: DataForChart[] } }) => {
    let measureToLabel = measuresShown[0];
    let isMeasureToLabel = measureToLabel == measure;
    let data = chartData[measure];
    let vals = data.map(d => d.y)
    //let [min, max] = [Math.min(...vals), Math.max(...vals)];
    for (let i = 1; i < 13; i++) {
        //let val = data[i].y;
        if (!isMeasureToLabel) {
            data[i].label = '';
            continue;
        }

        let val = parseFloat(data[i].label);
        if (isNaN(val)) continue;
        data[i].label = prepareNumber(val, 0);
    }
}

export const fixLabelOverlaps = ({ chartData, currentMeasures, measuresShown, domains, chartWidth }: { chartData: { [key: string]: DataForChart[] }, currentMeasures: string[], measuresShown: string[], domains: ChartDomains, chartWidth: number }) => {
    for (let measure of currentMeasures) {
        let measuresToLabel = measuresShown.slice(0);
        let isMeasureToLabel = _.includes(measuresToLabel, measure);
        let otherMeasures = isMeasureToLabel ? measuresToLabel.slice(0, measuresToLabel.indexOf(measure)) : [];
        let data = chartData[measure];
        let limitValue = (domains[0][1] - domains[0][0]) / 16;
        let isSmall = chartWidth < widthBreakpoints.roundedNumbers;
        let justOddValues = chartWidth < widthBreakpoints.oddValues;
        for (let i = 1; i < 13; i++) {
            let conflicting = false;
            let val = data[i].y;

            if (!isMeasureToLabel) {
                data[i].label = '';
                continue;
            }
            if (measure == 'ppt' && val < 0) {
                data[i].label = '';
                continue;
            }
            if (justOddValues && i % 2 == 0) {
                data[i].label = '';
                continue;
            }
            for (let otherMeasure of otherMeasures) {
                if (isSmall && otherMeasure == 'ppt') {
                    continue;
                }
                let otherVal = chartData[otherMeasure][i].y;
                if (otherVal == null) continue;
                if (Math.abs(val - otherVal) < limitValue) {
                    //console.log(measure, otherMeasure, val, otherVal)
                    conflicting = true;

                    break;
                }
            }
            if (conflicting || (isSmall && measure == 'ppt')) {
                data[i].label = '';
                continue;
            }

            if ((isSmall || measuresShown.length > 1) && (measure != 'ws')) {
                let val = parseFloat(data[i].label);
                if (isNaN(val)) continue;
                data[i].label = prepareNumber(val, 0);
            }
        }
    }
}

export const convertToDomain = ({ val, domain, domains, switched }: { val: number, domain: [number, number], domains: ChartDomains, switched?: boolean }): number => {
    let baseDomain = switched ? domain : domains[0];
    domain = switched ? domains[0] : domain;
    return ((val - domain[0]) * (baseDomain[1] - baseDomain[0]) / (domain[1] - domain[0]) + baseDomain[0])
}

export const getChartHeight = (clientWidth: number) => {
    for (let pair of widthBreakpoints.chartHeightBreakpoints) {
        if (pair[0] == 'default') return pair[1] as number;
        if (clientWidth < pair[0]) return pair[1] as number;
    }
}

export const calculateTickValues = ({ domains }: { domains: ChartDomains }): number[] => {
    let val = domains[0][0];
    let ticks = [];
    let diff = domains[0][1] - domains[0][0];
    let frequency = diff > 16 ? 5 : 2;
    do {
        if (val % frequency == 0) {
            ticks.push(val);
        }
        val++;
    } while (val <= domains[0][1]);
    return ticks;
}

export const checkStateValues = (state: { [key: string]: boolean }, weatherData: WeatherData) => {
    if (_.every(weatherData.sst, v => v == 0)) {
        state['sst'] = false;
    }
    if (_.every(weatherData.sunhrs, v => v == 0)) {
        state['sunhrs'] = false;
    }
    if (_.every(weatherData.rhm, v => v == 0)) {
        state['rhm'] = false;
    }
    if (_.every(weatherData.ws, v => v == 0)) {
        state['ws'] = false;
    }
}
export const calculateDomains = ({ weatherData, selectedUnit, fixedMeasures }: { weatherData: WeatherData, selectedUnit: string, fixedMeasures?: string[] }): ChartDomains => {

    if (fixedMeasures && fixedMeasures.length == 1) {
        let min = Math.min(...((weatherData) as any)[fixedMeasures[0]]);
        let max = Math.max(...((weatherData) as any)[fixedMeasures[0]]);
        if (min < 6) min = 0;
        if (fixedMeasures[0] == 'rhm') {
            return [[Math.floor((min - 5) / 5) * 5, Math.ceil((max + 5) / 5) * 5]]
        }
        else {
            return [[Math.max(0, Math.floor((min - 2) / 2) * 2), Math.ceil((max + 2) / 2) * 2]]
        }
    }

    let isF = selectedUnit == 'F';
    let toF = convertToFahrenheit;
    let [tmax, tmin, sst] = [weatherData.tmax.map(t => isF ? toF(t) : t), weatherData.tmin.map(t => isF ? toF(t) : t), weatherData.sst.map(t => isF ? toF(t) : t)]
    sst = _.compact(sst);

    let [rhm, ws, sunhrs, wet] = [_.compact(weatherData.rhm), _.compact(weatherData.ws), _.compact(weatherData.sunhrs), weatherData.wet];
    let ppt = weatherData.ppt.map(p => isF ? convertToInches(p) : p);

    let degMin = Math.min(...tmax, ...tmin, ...sst);
    let degMax = Math.max(...tmax, ...tmin, ...sst);
    let domain: [number, number];
    let hrsDomain: [number, number] = null;
    let percDomain: [number, number] = null;
    // for C main domain consists of tmin, tmax, sst; secondary rhm, ppt, possible tertially sunhrs, wet, ws - otherwise part of first
    // for F main domain consists of tmin, tmax, sst, rhm; secondary sunhrs, wet, ws, ppt
    if (selectedUnit === 'F') {
        if (!fixedMeasures) {
            degMin = Math.min(degMin, ...rhm);
            degMax = Math.max(degMax, ...rhm);
        }
        domain = [Math.floor((degMin - 10) / 10) * 10, Math.ceil((degMax + 10) / 10) * 10];
        let hrsMax = Math.max(...ppt);
        if (!fixedMeasures) {
            hrsMax = Math.max(...sunhrs, ...wet, ...ws, ...ppt);
        }
        hrsDomain = [0, Math.ceil(hrsMax + 1)]
    }
    else {
        domain = [Math.min(degMin > 17 ? 15 : 0, Math.floor((degMin - 2) / 2) * 2), Math.ceil((degMax + 2) / 2) * 2];
        let percMax = Math.max(...ppt, ...rhm);
        if (domain[0] > 0) {
            if (!fixedMeasures) {
                let hrsMax = Math.max(...sunhrs, ...wet, ...ws);
                hrsDomain = [0, Math.ceil(hrsMax + 1)]
            }
        }
        else {
            if (!fixedMeasures) {
                percMax = Math.max(percMax, ...sunhrs, ...wet, ...ws);
            }
        }

        percDomain = [0, Math.max(100, Math.ceil((percMax + 10) / 10) * 10)];
    }

    return _.compact([domain, percDomain, hrsDomain]);
}


export const calculateAxisLabels = ({ loc, domainMapping, measuresShown, selectedUnit }: { loc: (key: string) => string, domainMapping: string[][], measuresShown: string[], selectedUnit: string }): string[] => {

    let domainLabels = [];
    let temperatureIncluded = false;
    for (let domain of domainMapping) {
        let texts: string[] = [];

        domain = domain.filter(d => _.includes(measuresShown, d));
        if ((_.includes(domain, 'tmin') || _.includes(domain, 'tmax') || _.includes(domain, 'sst')) && !temperatureIncluded) {
            texts.push(firstCapitalCase(loc('common.temperature')) + ` (° ${selectedUnit})`);
            temperatureIncluded = true;
        }
        if (_.includes(domain, 'ws')) {
            texts.push(firstCapitalCase(loc('common.measures.ws')));
        }
        if (_.includes(domain, 'rhm')) {
            texts.push(firstCapitalCase(loc('common.measures.rhm')) + ' %');
        }
        if (_.includes(domain, 'sunhrs')) {
            texts.push(firstCapitalCase(loc('common.measures.sunhrs')));
        }
        if (_.includes(domain, 'ppt')) {
            texts.push(firstCapitalCase(loc('common.measures.ppt')) + ` (${selectedUnit == 'C' ? 'mm' : 'in'})`);
        }
        if (_.includes(domain, 'wet')) {
            texts.push(firstCapitalCase(loc('common.measures.wet')));
        }

        domainLabels.push(texts.join(', '));
    }
    return domainLabels

}

export interface DataForChart {
    x: number,
    y: number,
    y0?: number,
    forTooltip?: string,
    label: string,
    isArtificial: boolean
}

export type ChartDomains = [number, number][];
